import { useState, useCallback } from 'react';

export interface SMSConfig {
  phoneNumbers: string[];
  enabled: boolean;
}

export interface SMSResult {
  success: boolean;
  message: string;
  sentTo?: string[];
  errors?: Array<{ phone: string; error: string }>;
}

export interface VitalSignsForSMS {
  pulseRate: { value?: any; isEnabled: boolean };
  respirationRate: { value?: any; isEnabled: boolean };
  oxygenSaturation: { value?: any; isEnabled: boolean };
  bloodPressure: { value?: any; isEnabled: boolean };
  stressLevel: { value?: any; isEnabled: boolean };
  wellnessLevel: { value?: any; isEnabled: boolean };
  heartAge: { value?: any; isEnabled: boolean };
  [key: string]: any;
}

const formatVitalSignsForSMS = (vitalSigns: VitalSignsForSMS): string => {
  const timestamp = new Date().toLocaleString();
  let message = `ContinuumLink Health Scan Results\n${timestamp}\n\n`;

  // Primary vital signs
  if (
    vitalSigns.pulseRate?.isEnabled &&
    vitalSigns.pulseRate?.value !== null &&
    vitalSigns.pulseRate?.value !== undefined
  ) {
    message += `- Pulse: ${vitalSigns.pulseRate.value} bpm\n`;
  }

  if (
    vitalSigns.respirationRate?.isEnabled &&
    vitalSigns.respirationRate?.value !== null &&
    vitalSigns.respirationRate?.value !== undefined
  ) {
    message += `- Breathing: ${vitalSigns.respirationRate.value} rpm\n`;
  }

  if (
    vitalSigns.oxygenSaturation?.isEnabled &&
    vitalSigns.oxygenSaturation?.value !== null &&
    vitalSigns.oxygenSaturation?.value !== undefined
  ) {
    message += `- Oxygen: ${vitalSigns.oxygenSaturation.value}%\n`;
  }

  if (vitalSigns.bloodPressure?.isEnabled && vitalSigns.bloodPressure?.value) {
    const bp = vitalSigns.bloodPressure.value;
    if (bp.systolic && bp.diastolic) {
      message += `- Blood Pressure: ${bp.systolic}/${bp.diastolic} mmHg\n`;
    }
  }

  // Wellness indicators
  if (
    vitalSigns.stressLevel?.isEnabled &&
    vitalSigns.stressLevel?.value !== null &&
    vitalSigns.stressLevel?.value !== undefined
  ) {
    message += `- Stress: ${vitalSigns.stressLevel.value}\n`;
  }

  if (
    vitalSigns.wellnessLevel?.isEnabled &&
    vitalSigns.wellnessLevel?.value !== null &&
    vitalSigns.wellnessLevel?.value !== undefined
  ) {
    message += `- Wellness: ${vitalSigns.wellnessLevel.value}\n`;
  }

  if (
    vitalSigns.heartAge?.isEnabled &&
    vitalSigns.heartAge?.value !== null &&
    vitalSigns.heartAge?.value !== undefined
  ) {
    message += `- Heart Age: ${vitalSigns.heartAge.value} years\n`;
  }

  message += '\nSent from ContinuumLink Health Scanner';
  return message;
};

const buildTestMessage = () => {
  const timestamp = new Date().toLocaleString();
  return `ContinuumLink SMS connectivity test\n${timestamp}\n\nIf you received this message, SMS delivery is working.`;
};

interface SendOptions {
  getSuccessMessage?: (details: {
    sentTo: string[];
    totalRequested: number;
  }) => string;
}

export const useSMS = () => {
  const [isSending, setIsSending] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<SMSResult | null>(null);

  const sendSMSRequest = useCallback(
    async (
      message: string,
      config: SMSConfig,
      options?: SendOptions,
    ): Promise<SMSResult> => {
      if (!config.enabled || !config.phoneNumbers.length) {
        const result: SMSResult = {
          success: false,
          message: 'SMS not configured or disabled',
        };
        setLastResult(result);
        return result;
      }

      setIsSending(true);

      try {
        const response = await fetch('/api/send-sms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message,
            phoneNumbers: config.phoneNumbers,
          }),
        });

        const rawBody = await response.text();
        let data: any = null;

        if (rawBody) {
          try {
            data = JSON.parse(rawBody);
          } catch (parseError) {
            console.error('Unable to parse SMS response JSON:', parseError);
          }
        }

        if (response.ok) {
          const sentTo: string[] = Array.isArray(data?.sentTo)
            ? data.sentTo
            : config.phoneNumbers;

          const result: SMSResult = {
            success: true,
            message:
              options?.getSuccessMessage?.({
                sentTo,
                totalRequested: config.phoneNumbers.length,
              }) ??
              `SMS sent successfully to ${sentTo.length} recipient(s)`,
            sentTo,
            errors: Array.isArray(data?.errors) ? data.errors : undefined,
          };

          setLastResult(result);
          return result;
        }

        const result: SMSResult = {
          success: false,
          message:
            (typeof data?.error === 'string' && data.error) ||
            (typeof data?.message === 'string' && data.message) ||
            `Request failed with status ${response.status}`,
          errors: Array.isArray(data?.errors) ? data.errors : undefined,
        };

        setLastResult(result);
        return result;
      } catch (error) {
        console.error('SMS sending error:', error);
        const result: SMSResult = {
          success: false,
          message: 'Network error while sending SMS',
        };
        setLastResult(result);
        return result;
      } finally {
        setIsSending(false);
      }
    },
    [setIsSending, setLastResult],
  );

  const sendHealthResults = useCallback(
    async (
      vitalSigns: VitalSignsForSMS,
      config: SMSConfig,
    ): Promise<SMSResult> => {
      const smsMessage = formatVitalSignsForSMS(vitalSigns);
      return sendSMSRequest(smsMessage, config, {
        getSuccessMessage: ({ sentTo }) =>
          `SMS sent successfully to ${sentTo.length} recipient(s)`,
      });
    },
    [sendSMSRequest],
  );

  const sendTestMessage = useCallback(
    async (config: SMSConfig): Promise<SMSResult> => {
      const smsMessage = buildTestMessage();
      return sendSMSRequest(smsMessage, config, {
        getSuccessMessage: ({ sentTo, totalRequested }) =>
          `Test SMS sent to ${sentTo.length} of ${totalRequested} configured recipient(s)`,
      });
    },
    [sendSMSRequest],
  );

  return {
    sendHealthResults,
    sendTestMessage,
    isSending,
    lastResult,
  };
};
